import { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { UserContext } from 'context/User/UserContext';
import { getMenuItems } from '../_api';
import { alpha, styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import IMSLink from '../Links/IMSLink';
import { determineIfExternal, getPath } from 'routes/_utils';

const Search = styled('div')(({ theme }) => ({
	position: 'relative',
	borderRadius: theme.shape.borderRadius,
	backgroundColor: alpha(theme.palette.common.white, 0.15),
	'&:hover': {
		backgroundColor: alpha(theme.palette.common.white, 0.25),
	},
	marginRight: theme.spacing(5),
	marginLeft: 0,
	width: '50%',
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
	padding: theme.spacing(0, 2),
	height: '100%',
	position: 'absolute',
	pointerEvents: 'none',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
}));

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
	color: 'inherit',
	padding: theme.spacing(0, 1, 1, 0),
	// vertical padding + font size from searchIcon
	paddingLeft: `calc(1em + ${theme.spacing(4)})`,
	transition: theme.transitions.create('width'),
	width: '100%',
	'& .MuiFilledInput-root': {
		color: 'inherit',
		padding: theme.spacing(1, 1, 1, 0),
		backgroundColor: 'inherit',
		'&:hover': {
			backgroundColor: 'inherit',
		},
		'&:focus': {
			backgroundColor: 'inherit',
		},
		// vertical padding + font size from searchIcon
		paddingLeft: `calc(1em + ${theme.spacing(4)})`,
		transition: theme.transitions.create('width'),
		width: '100%',
	},
	'& .MuiSvgIcon-root': {
		color: theme.palette.common.white,
		marginTop: '4px',
	},
}));

const AutoCompleteInputSearch = () => {
	const [menuItems, setMenuItems] = useState([]);
	const [autoCompleteValue, setAutoCompleteValue] = useState({});
	const history = useHistory();
	/**
	 * @type {import('context/User/UserContext').ContextUser}
	 */
	const contextUser = useContext(UserContext);
	const user =
		contextUser.userInfo.impersonationProfile ??
		contextUser.userInfo.userProfile;

	useEffect(() => {
		if (user?.Roles) {
			getMenuItems(user.ID).then(res => {
				let cleanRes = res.data
					? res.data.sort((a, b) => {
							if (a.Name < b.Name) return -1;
							if (a.Name > b.Name) return 1;
							return 0;
					  })
					: [];
				cleanRes = cleanRes.filter(
					(value, index, self) =>
						index ===
						self.findIndex(
							t =>
								t.Name + t.ArgumentPage ===
								value.Name + value.ArgumentPage
						)
				);

				setMenuItems(cleanRes);
			});
		}
	}, [user]);

	const handleSearchChange = (_, newValue) => {
		setAutoCompleteValue(newValue);
	};

	/**
	 * Filter the options list based on search result relevance
	 * @param {Array} options list of options passed to the autocomplete
	 * @param {Object} state contains `inputValue` which is what the user typed into the search box
	 * @returns filtered and sorted options list
	 */
	const filterOptions = (options, { inputValue }) => {
		if (!inputValue) {
			return options;
		}
		const newOptions = options
			.filter(opt =>
				(getImmediateParentName(opt) + opt.Name)
					.toLowerCase()
					.includes(inputValue.toLowerCase())
			)
			.sort((a, b) => {
				return a.Name - b.Name;
			});
		return newOptions;
	};

	useEffect(() => {
		if (autoCompleteValue?.ArgumentPage) {
			if (determineIfExternal(autoCompleteValue.ArgumentPage.trim())) {
				window.top.location.href =
					autoCompleteValue.ArgumentPage.trim();
			} else {
				history.push(getPath(autoCompleteValue.ArgumentPage));
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [autoCompleteValue]);

	const getImmediateParentName = option => {
		let parent = menuItems.find(x => x.ID === option.ParentID);
		if (parent) {
			return parent.Name + ' > ';
		} else {
			return '';
		}
	};

	return (
		<Search>
			<SearchIconWrapper>
				<SearchIcon />
			</SearchIconWrapper>
			<StyledAutocomplete
				id='search-menu-input'
				options={menuItems}
				filterOptions={filterOptions}
				getOptionLabel={option => option.Name}
				onChange={handleSearchChange}
				size={'small'}
				renderInput={params => (
					<TextField
						{...params}
						variant='filled'
						placeholder={'Search...'}
						InputLabelProps={{ shrink: false }}
						InputProps={{
							...params.InputProps,
							disableUnderline: true,
						}}
					/>
				)}
				renderOption={(props, option, { inputValue }) => {
					const matches = match(
						getImmediateParentName(option) + option.Name,
						inputValue
					);
					const parts = parse(
						getImmediateParentName(option) + option.Name,
						matches
					);
					return (
						<>
							{!option.IsParentItem ? (
								<li {...props}>
									<IMSLink url={option.ArgumentPage}>
										<div>
											{parts.map((part, index) => (
												<span
													key={index}
													style={{
														fontWeight:
															part.highlight
																? 700
																: 400,
													}}
												>
													{part.text}
												</span>
											))}
										</div>
									</IMSLink>
								</li>
							) : null}
						</>
					);
				}}
			/>
		</Search>
	);
};

export default AutoCompleteInputSearch;
